//#region IMPORT
import React, { useCallback, useEffect, useRef, useState } from 'react';
import M_TBG_BASIC_07 from './M_TBG_BASIC_07';
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { VoiceRecorder } from 'capacitor-voice-recorder';
import { Capacitor } from '@capacitor/core';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions } from '../../../Reducer/CommonReducer';
//#endregion

function M_TBG_BASIC_07F(props) {
  //#region 변수
  const { userInfo, lanInfo } = useSelector((state) => state.BaseInfo);
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => state.common.activeStep);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info',
  });
  const [codeList, setCodeList] = useState([]); // 코드 list
  const [rowData, setRowData] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState('00:00');
  const [recordedFile, setRecordedFile] = useState(null);
  const [gjList, setGjList] = useState([]);
  const [state, setState] = useState({
    bottom: false,
    type: '',
  }); // 드로워 상태값
  const [duration, setDuration] = useState(600);
  const [current, setCurrent] = useState(0);

  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const recordRef = useRef(null);
  const imgRef = useRef([]);

  // Native용 녹음 관련 변수
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  //#endregion

  useEffect(() => {
    Init();
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const elapsedSeconds = Math.floor(
        (now.getTime() - props.startTimeRef.current?.getTime()) / 1000
      );
      setCurrent(elapsedSeconds);
    };

    updateTime(); // 초기 1회 호출
    const interval = setInterval(updateTime, 1000); // 1초마다 갱신

    return () => clearInterval(interval); // 언마운트 시 정리
  }, [props.startTimeRef.current]);

  // PWA용 WaveSurfer 초기화
  const InitWaveSurfer = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#A8DBA8',
      progressColor: '#3B8686',
      cursorColor: '#3B8686',
      height: 100,
    });

    const record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        renderRecordedAudio: true,
        scrollingWaveform: true,
        continuousWaveformDuration: 30,
      })
    );

    record.on('record-progress', (time) => {
      setProgress(FormatTime(time));
    });

    record.on('record-end', (blob) => {
      const url = URL.createObjectURL(blob);
      wavesurferRef.current.empty();
      wavesurferRef.current.load(url);

      const audioFile = new File(
        [blob],
        `${new Date()
          .toISOString()
          .replace(/T/, '')
          .replace(/\..+/, '')
          .replace(/-/g, '')
          .replace(/:/g, '')}.mp3`,
        {
          type: 'audio/mp3',
        }
      );

      setRecordedFile(audioFile);

      wavesurferRef.current.on('finish', () => {
        setIsPlaying(false);
      });
    });

    recordRef.current = record;
    wavesurferRef.current = wavesurfer;
    setProgress('00:00');
  }, [
    wavesurferRef,
    waveformRef,
    recordRef,
    setProgress,
    setRecordedFile,
    setIsPlaying,
  ]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      InitWaveSurfer();
    } else {
      // Native 플랫폼에서는 WaveSurfer 초기화를 건너뛰고 초기 상태만 설정
      setProgress('00:00');
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
      // Native 리스너도 정리
      if (Capacitor.isNativePlatform()) {
        VoiceRecorder.removeAllListeners();
      }
    };
  }, [InitWaveSurfer, setProgress, wavesurferRef]);

  const Init = async () => {
    setLoading(true);

    try {
      await SearchLogic1();
      await SearchCode();
    } catch (e) {
      console.log(e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  //#region 조회
  //코드정보 조회
  const SearchCode = async () => {
    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.execute,
        [
          {
            procedureName: 'SP_GetCode',
          },
        ]
      ).then((result) => {
        let data = result.data;
        if (data.length === 0) {
          setCodeList([]);
        } else if (data[0]?.type === 'Error') {
          //Get할 땐 에러인 경우 인덱스 0 에 {type:"Error"} 로 반환되고, 에러가 아닐 경우엔 정상적인 데이터 반환
          throw new Error(data[0].message);
        } else {
          setCodeList(data);
        }
      });
    } catch (e) {
      console.log(e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    } finally {
    }
  };
  //사진 가져오기
  const SearchPhoto = async (_list, _item, _index) => {
    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.ftpResponse,
        {
          value1: Commons.EncryptAES(_item.PHOTO),
        }
      ).then((result) => {
        let data = result.data;

        if (data !== 'Error') {
          imgRef.current.push({
            ID: _item.ID,
            PROFILE: `data:image/png;base64,${data}`,
          });

          setRowData(
            Commons.InsertNumber(
              _list.map((item) => {
                const matched = imgRef.current.find(
                  (img) => img.ID === item.ID
                );
                return matched ? { ...item, PROFILE: matched.PROFILE } : item;
              })
            )
          );
        }
      });
    } catch (e) {
      console.log(e);
    } finally {
    }
  };
  // TBM회의 참석인원 조회
  const SearchLogic1 = async () => {
    setLoading(true);
    setRowData([]);
    imgRef.current = [];

    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.execute,
        [
          {
            procedureName: 'SP_M20903_R1',
            _tbmCd: Commons.EncryptAES(props.tbmCdRef.current),
          },
        ]
      ).then((result) => {
        let data = result.data;
        if (data[0].type === 'Error') {
          throw new Error(data[0].message);
        } else {
          data.forEach((item, index) => {
            item.PHOTO && SearchPhoto(data, item, index);
          });
        }
      });
    } catch (e) {
      console.log(e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  // 공정 조회
  const SearchGj = async () => {
    setLoading(true);
    setGjList([]);

    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.execute,
        [
          {
            procedureName: 'SP_SITE_GJ_R1',
            _userid: userInfo.userid,
            _authCd: Commons.EncryptAES(userInfo.authCd) || '',
          },
        ]
      ).then((result) => {
        let data = result.data;

        if (data.length > 0) {
          if (data[0].type === 'Error') {
            throw new Error(data[0].message);
          } else {
            // 데이터를 원하는 형식으로 변환
            const formattedData = data
              .map((item) => {
                // GJ_CD와 GJ_NM을 콤마로 분리
                const gjCdList = item.GJ_CD.split(',');
                const gjNmList = item.GJ_NM.split(',');

                // 결과 배열 생성
                return gjCdList.map((gjCd, index) => ({
                  GJ_CD: gjCd,
                  GJ_NM: gjNmList[index],
                }));
              })
              .flat();

            setGjList(formattedData);
          }
        } else {
          setSnackbar({
            ...snackbar,
            open: true,
            message: '공정이 등록되지 않았습니다. 관리자에게 문의하세요.',
            type: 'warning',
          });
        }
      });
    } catch (e) {
      console.log(e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  //#endregion
  //#region 저장
  // 녹음파일 저장
  const SaveLogic1 = async () => {
    setLoading(true);
    try {
      if (!recordedFile) {
        setSnackbar({
          ...snackbar,
          open: true,
          message: '녹음된 파일이 없습니다.',
          type: 'warning',
        });
        return false;
      }

      const targetPath = `TBM/${props.tbmCdRef.current}/Audio`;
      const formList = new FormData();
      formList.append('UserId', userInfo.userid);
      formList.append('FilePath', targetPath);
      formList.append('FormFile', recordedFile);

      const result = await Commons.Axios(
        'file2',
        Global.file.useUrl + Global.urls.Execute.videoUpload,
        null,
        formList
      );

      let finalPath = result?.data?.filePath || '';

      const duration = await GetAudioDurationFromFile(recordedFile);
      const durationSeconds = Math.floor(duration);

      await Commons.Axios(
        'post1',
        Global.base.useUrl + Global.urls.Execute.execute,
        [
          {
            procedureName: 'SP_M20906_U1',
            _tbmCd: Commons.EncryptAES(props.tbmCdRef.current) || '',
            _userid: userInfo.userid,
            _tbmAddMeeting:
              Commons.EncryptAES(
                `TBM/${props.tbmCdRef.current}/Audio/${recordedFile.name}`
              ) || '',
            _tbmAddMeetingTime:
              Commons.EncryptAES(durationSeconds.toString()) || '',
          },
        ]
      ).then((result) => {
        let data = result.data;
        if (data[0]?.type === 'Ok') {
          setSnackbar({
            ...snackbar,
            open: true,
            message: '저장되었습니다.',
            type: 'success',
          });
          dispatch(CommonActions.setActiveStep(activeStep + 1));
          SaveLogic2(2, 7);
        } else {
          throw new Error(data[0].message || 'DB 저장 실패');
        }
      });
    } catch (e) {
      console.error('Error: ', e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // tbmstatus 교육현황 저장
  const SaveLogic2 = async (_eStep, _mStep) => {
    setLoading(true);
    let saveData = [];

    try {
      saveData.push({
        procedureName: 'SP_TBM_STATUS_U1',
        _TBM_CD: Commons.EncryptAES(props.tbmCdRef.current) || '', // TBM코드
        _E_STEP: Commons.EncryptAES(_eStep.toString()) || '', // 근로자 교육 페이지
        _M_STEP: Commons.EncryptAES(_mStep.toString()) || '', // 관리자 교육 페이지
        _USER_ID: userInfo.userid, // 참석자ID
      });

      if (saveData.length > 0) {
        await Commons.Axios(
          'post1',
          Global.base.useUrl + Global.urls.Execute.execute,
          saveData
        ).then(async (result) => {
          let data = result?.data[0];

          if (data.type === 'Error') {
            throw new Error(data.message);
          } else {
            // setSnackbar({
            //   open: true,
            //   type: "success",
            //   message: "저장되었습니다.",
            // });
            // setModalToggle1(true);
          }
        });
      }
    } catch (e) {
      console.log(e);
      setSnackbar({
        ...snackbar,
        open: true,
        message: e.message,
        type: 'error',
      });
    }
  };
  //#endregion
  //#region 삭제
  //#endregion
  //#region 나머지 함수
  // 녹음 길이 확인 함수
  const GetAudioDurationFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';

      audio.onloadedmetadata = () => {
        // Chrome에서는 metadata 로드 후에도 duration이 0일 수 있으므로 다음 코드 추가
        if (audio.duration === Infinity) {
          audio.currentTime = 1e101;
          audio.ontimeupdate = () => {
            audio.ontimeupdate = null;
            resolve(audio.duration);
            audio.currentTime = 0;
          };
        } else {
          resolve(audio.duration);
        }
      };

      audio.onerror = () => {
        reject(new Error('오디오 파일을 읽을 수 없습니다.'));
      };

      audio.src = URL.createObjectURL(file);
    });
  };
  // 드로워 상태값 관리
  const ToggleDrawer = (anchor, open, gubun) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setState({ ...state, [anchor]: open, gubun: gubun });
  };

  const FormatTime = (ms) => {
    const min = Math.floor((ms % 3600000) / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return `${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec}`;
  };

  // Native용 녹음 시간 업데이트
  const UpdateNativeRecordingTime = () => {
    if (recordingStartTimeRef.current) {
      const elapsed = Date.now() - recordingStartTimeRef.current;
      const formattedTime = FormatTime(elapsed);
      setProgress(formattedTime);
      animationFrameRef.current = requestAnimationFrame(
        UpdateNativeRecordingTime
      );
    }
  };

  // Native용 녹음 시작
  const StartNativeRecording = async () => {
    try {
      const canRecord = await VoiceRecorder.canDeviceVoiceRecord();
      if (!canRecord.value) {
        throw new Error('Cannot record on this device');
      }

      const hasPerm = await VoiceRecorder.hasAudioRecordingPermission();
      if (!hasPerm.value) {
        const permRes = await VoiceRecorder.requestAudioRecordingPermission();
        if (!permRes.value) {
          throw new Error('Microphone permission denied');
        }
      }

      await VoiceRecorder.startRecording();

      // UI 상태 업데이트
      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();

      // 시간 업데이트 시작
      UpdateNativeRecordingTime();
    } catch (error) {
      console.error('[Capacitor] Native recording start error:', error);
      setIsRecording(false);
    }
  };

  // PWA용 녹음 시작
  const StartPWARecording = async () => {
    if (!recordRef.current) return;
    try {
      await recordRef.current.startRecording();
      setIsRecording(true);
    } catch (error) {
      console.error('PWA recording start error:', error);
      setIsRecording(false);
    }
  };

  // 통합 녹음 시작 함수
  const StartRecording = async () => {
    if (Capacitor.isNativePlatform()) {
      await StartNativeRecording();
    } else {
      await StartPWARecording();
    }
  };

  // Native용 녹음 종료
  const StopNativeRecording = async () => {
    try {
      // 먼저 UI 상태를 업데이트
      setIsRecording(false);

      // 애니메이션 프레임 정리
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // 녹음 중지
      const data = await VoiceRecorder.stopRecording();

      // 시작 시간 초기화
      if (recordingStartTimeRef.current) {
        recordingStartTimeRef.current = null;
      }

      if (data.value && data.value.recordDataBase64) {
        const blob = Base64toBlob(data.value.recordDataBase64, 'audio/mp3');
        const file = new File(
          [blob],
          `${new Date()
            .toISOString()
            .replace(/T/, '')
            .replace(/\..+/, '')
            .replace(/-/g, '')
            .replace(/:/g, '')}.mp3`,
          { type: 'audio/mp3' }
        );

        // 녹음된 파일에서 파형 분석하여 그리기
        AnalyzeAndDrawWaveform(blob);

        setRecordedFile(file);
      }
    } catch (error) {
      console.error('[Capacitor] Native recording stop error:', error);
      setIsRecording(false);

      // 에러 발생 시에도 정리 작업 수행
      if (recordingStartTimeRef.current) {
        recordingStartTimeRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  };

  // PWA용 녹음 종료
  const StopPWARecording = () => {
    if (!recordRef.current) return;
    recordRef.current.stopRecording();
    setIsRecording(false);
  };

  // 통합 녹음 종료 함수
  const StopRecording = async () => {
    if (Capacitor.isNativePlatform()) {
      await StopNativeRecording();
    } else {
      StopPWARecording();
    }
  };

  // 녹음 재생
  const PlayRecording = () => {
    if (Capacitor.isNativePlatform()) {
      // Native 플랫폼에서는 간단한 Audio 객체 사용
      if (!recordedFile) return;

      const audio = new Audio(URL.createObjectURL(recordedFile));
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
        audio.onended = () => setIsPlaying(false);
      }
    } else {
      // PWA에서는 WaveSurfer 사용
      if (!wavesurferRef.current) return;
      wavesurferRef.current.playPause();
      setIsPlaying(wavesurferRef.current.isPlaying());
    }
  };

  // 녹음된 파일에서 파형 분석하여 그리기
  const AnalyzeAndDrawWaveform = async (blob) => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // AudioContext를 사용하여 오디오 데이터 분석
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const channelData = audioBuffer.getChannelData(0); // 첫 번째 채널
      const amp = canvas.height / 2;

      // 파형 그리기
      ctx.beginPath();
      ctx.strokeStyle = '#3B8686';
      ctx.lineWidth = 1.5;

      const step = Math.ceil(channelData.length / canvas.width);

      for (let i = 0; i < canvas.width; i++) {
        let min = 1.0;
        let max = -1.0;

        for (let j = 0; j < step; j++) {
          const idx = i * step + j;
          if (idx < channelData.length) {
            const datum = channelData[idx];
            if (datum < min) min = datum;
            if (datum > max) max = datum;
          }
        }

        // 파형 그리기
        ctx.moveTo(i, (1 + min) * amp);
        ctx.lineTo(i, (1 + max) * amp);
      }

      ctx.stroke();

      // 중앙선 그리기
      ctx.beginPath();
      ctx.strokeStyle = '#A8DBA8';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.moveTo(0, amp);
      ctx.lineTo(canvas.width, amp);
      ctx.stroke();
      ctx.setLineDash([]);
    } catch (error) {
      console.error('[Capacitor] Error analyzing waveform:', error);
    }
  };

  // Base64 → Blob 변환 헬퍼
  const Base64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };
  //#endregion
  return (
    <M_TBG_BASIC_07
      userInfo={userInfo}
      codeList={codeList}
      setCodeList={setCodeList}
      rowData={rowData}
      setRowData={setRowData}
      isRecording={isRecording}
      setIsRecording={setIsRecording}
      isPlaying={isPlaying}
      setIsPlaying={setIsPlaying}
      progress={progress}
      setProgress={setProgress}
      recordedFile={recordedFile}
      setRecordedFile={setRecordedFile}
      gjList={gjList}
      setGjList={setGjList}
      state={state}
      setState={setState}
      duration={duration}
      setDuration={setDuration}
      current={current}
      setCurrent={setCurrent}
      waveformRef={waveformRef}
      wavesurferRef={wavesurferRef}
      recordRef={recordRef}
      canvasRef={canvasRef}
      // 함수
      SearchGj={SearchGj}
      SaveLogic1={SaveLogic1}
      ToggleDrawer={ToggleDrawer}
      StartRecording={StartRecording}
      StopRecording={StopRecording}
      PlayRecording={PlayRecording}
    />
  );
}

export default M_TBG_BASIC_07F;
